import type { CSSObject } from '@emotion/react'
import { MapItemsCache } from 'components/GoogleMap/MapItemsCache'
import type { ReactComponentOverlayView } from 'components/GoogleMap/_utils/makeReactComponentOverlayView'
import { makeReactComponentOverlayView } from 'components/GoogleMap/_utils/makeReactComponentOverlayView'
import { captureFrontendError } from 'utils/captureFrontendError'
import type { TPropertyBadgeComponentProps } from './PropertyBadgeComponent/PropertyBadgeComponent'
import { PropertyBadgeComponent } from './PropertyBadgeComponent/PropertyBadgeComponent'

export type PROPERTY_META = {
  propertyName: string
  propertyId: string
  bounds: google.maps.LatLngBounds
}

// TODO: will eventually come from current values type
export type TPropertiesValueTypesToPropsDictionary = Record<
  string,
  {
    baseZIndex?: number
    children: React.ReactNode
    getContainerCSS: () => CSSObject
    onHover: boolean
  }
>

/**
 * Why `bounds` can yield no corner is still unknown (DEV-5178). The paradox is
 * that these items already passed `bounds.intersects(currentBounds)` in
 * `getItemsToProcess`, so the object is functional enough to filter with, yet
 * `getNorthEast()` comes back undefined — not behaviour a real `LatLngBounds`
 * should exhibit.
 *
 * Capture enough shape to settle it on the next occurrence rather than guessing
 * again. Reported once per property per session: `process()` runs on every map
 * idle, so an unconditional capture would flood the project.
 */
const reportedUnplaceableProperties = new Set<string>()

const reportUnplaceableProperty = (meta: PROPERTY_META) => {
  if (reportedUnplaceableProperties.has(meta.propertyId)) return

  reportedUnplaceableProperties.add(meta.propertyId)

  // Diagnostics must never be the thing that throws here.
  try {
    const bounds: unknown = meta.bounds

    captureFrontendError(new Error('Property bounds yielded no corner'), {
      extra: {
        propertyId: meta.propertyId,
        boundsType: typeof bounds,
        boundsConstructor: (bounds as { constructor?: { name?: string } })?.constructor?.name ?? null,
        boundsIsEmpty: (bounds as { isEmpty?: () => boolean })?.isEmpty?.() ?? null,
        boundsJSON: (bounds as { toJSON?: () => unknown })?.toJSON?.() ?? null,
        boundsKeys: bounds && typeof bounds === 'object' ? Object.keys(bounds).slice(0, 20) : null,
        hasGetNorthEast: typeof (bounds as { getNorthEast?: unknown })?.getNorthEast === 'function',
        hasGetCenter: typeof (bounds as { getCenter?: unknown })?.getCenter === 'function',
        mapsVersion: window.google?.maps?.version ?? null,
      },
      tags: { area: 'currentValuesMap' },
    })
  } catch {
    captureFrontendError(new Error('Property bounds yielded no corner (diagnostics failed)'))
  }
}

// export type BLOCK_SCDS_VALUE = Record<SCD_LAT_LNG, Record<string, COMPONENT_PROPS>>
export class PropertyValuesCache extends MapItemsCache<
  PROPERTY_META,
  TPropertiesValueTypesToPropsDictionary,
  ReactComponentOverlayView<TPropertyBadgeComponentProps>
> {
  constructor(options: { maps: typeof google.maps; map: google.maps.Map }) {
    super({
      createOverlay: (o) => {
        const northEast = o.meta.bounds?.getNorthEast()
        const center = o.meta.bounds?.getCenter()

        // Some properties reach this point with bounds that yield no corner,
        // and the resulting throw aborted the whole overlay pass — every
        // property still queued behind this one silently lost its badge.
        // Skip the unplaceable property instead. See BANYAN_APP-4NK / -4GG.
        if (!northEast || !center) {
          reportUnplaceableProperty(o.meta)

          return undefined
        }

        return makeReactComponentOverlayView({
          component: PropertyBadgeComponent,
          transform: 'translate(-50%, -110%)',
          latLng: {
            lat: northEast.lat(),
            lng: center.lng(),
          },
          maps: o.maps,
          map: o.map,
        })
      },
      renderOverlay: (o) => {
        o.overlay.render({ ...o, valueTypesToPropsDictionary: o.value })
      },
      hideOverlay: (o) => {
        o.overlay.setMap(null)
      },
      maps: options.maps,
      map: options.map,
    })
  }
}
